import React from 'react';
import {
  Box,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Icon,
  HStack,
  Skeleton,
} from '@chakra-ui/react';
import { FaMicrophone, FaLayerGroup, FaHeadphones } from 'react-icons/fa';

const StatBox = ({ icon, label, value, isLoading, gradientFrom, gradientTo }) => (
  <Stat
    px={{ base: 5, md: 8 }}
    py={{ base: 6, md: 8 }}
    background="rgba(255,150,50,0.04)"
    border="1px solid rgba(255,150,50,0.08)"
    backdropFilter="blur(12px)"
    rounded="2xl"
    transition="all 0.35s ease"
    position="relative"
    overflow="hidden"
    _hover={{
      transform: 'translateY(-6px)',
      border: '1px solid rgba(255,152,0,0.35)',
      boxShadow: `0 20px 60px rgba(255, 152, 0, 0.15), 0 0 0 1px rgba(255,152,0,0.08)`,
    }}
  >
    {/* Top gradient line */}
    <Box
      position="absolute"
      top={0}
      left={0}
      right={0}
      h="2px"
      bgGradient={`linear(to-r, ${gradientFrom}, ${gradientTo})`}
    />

    {/* Subtle inner glow */}
    <Box
      position="absolute"
      top="-40px"
      right="-20px"
      w="120px"
      h="120px"
      borderRadius="full"
      bg={`${gradientFrom}22`}
      filter="blur(30px)"
      pointerEvents="none"
    />

    <Box position="relative" zIndex={1}>
      <HStack spacing={3} mb={4}>
        <Box
          p={2.5}
          borderRadius="lg"
          bg="rgba(255,152,0,0.1)"
          border="1px solid rgba(255,152,0,0.2)"
        >
          <Icon as={icon} w={5} h={5} color="brand.400" />
        </Box>
        <StatLabel
          fontSize={{ base: "sm", md: "md" }}
          fontWeight="medium"
          color="whiteAlpha.700"
          letterSpacing="wide"
          textTransform="uppercase"
        >
          {label}
        </StatLabel>
      </HStack>

      {isLoading ? (
        <Skeleton height="52px" width="120px" />
      ) : (
        <StatNumber
          fontSize={{ base: "4xl", md: "5xl" }}
          fontWeight="extrabold"
          bgGradient={`linear(to-r, ${gradientFrom}, ${gradientTo})`}
          bgClip="text"
          lineHeight="1"
        >
          {value}
        </StatNumber>
      )}
    </Box>
  </Stat>
);

const StatsCard = ({ stats, isLoading = false }) => {
  return (
    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 4, md: 6 }} maxW="5xl" mx="auto" w="full">
      <StatBox
        icon={FaMicrophone}
        label="Total Episodes"
        value={stats?.totalEpisodes || 0}
        isLoading={isLoading}
        gradientFrom="brand.400"
        gradientTo="orange.300"
      />
      <StatBox
        icon={FaLayerGroup}
        label="Total Seasons"
        value={stats?.totalSeasons || 0}
        isLoading={isLoading}
        gradientFrom="terracotta.400"
        gradientTo="terracotta.300"
      />
      <StatBox
        icon={FaHeadphones}
        label="Hours of Content"
        value={`${stats?.duration?.hours || 0}h ${stats?.duration?.minutes || 0}m`}
        isLoading={isLoading}
        gradientFrom="brand.300"
        gradientTo="terracotta.400"
      />
    </SimpleGrid>
  );
};

export default StatsCard;
